import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
// Liquid Glass sits on top of the monochrome base, so it loads after it
import './glass.css';
// The service row and the Films/Series page, built on the glass tokens
import './browse.css';
// The player's progress bar while it is being dragged
import './scrub.css';
// Phones: the touch player, bottom sheets, tab bar and safe areas. Last, so
// it is the layer that answers for a finger
import './mobile.css';
import { AuthProvider, useAuth } from './lib/Auth';
import { LibraryProvider } from './lib/Library';
import { CatalogProvider } from './lib/Catalog';
import Login from './components/Login';
import Shell from './components/Shell';
import Home from './components/Home';
import Discover from './components/Discover';
import Title from './components/Title';
import Person from './components/Person';
import Search from './components/Search';
import MyList from './components/MyList';
import Live from './components/Live';
import LiveTv from './components/LiveTv';
import Data from './components/Data';
import Player from './components/Player';
import Profiles from './components/Profiles';
import TorrentDetails from './components/TorrentDetails';
import Intro from './components/Intro';
import Signup from './components/Signup';
import Admin from './components/Admin';
import TvLink from './components/TvLink';
import './admin.css';

/**
 * Everything except the login screen is behind a session, and behind a profile.
 *
 * The server refuses the API without one regardless — this only decides what to
 * render, so nobody stares at an empty shell whose every request is coming back
 * 401. Where they were going is carried into the login screen so signing in
 * resumes it.
 *
 * The profile check sits here rather than on one route because every protected
 * page reads a library, and a library needs to know whose. An account with a
 * single profile is entered without being asked; one with several shows the
 * picker until somebody is chosen, including on a deep link straight to a film.
 */
const RequireAuth = ({ children }) => {
  const { user, ready } = useAuth();
  const location = useLocation();

  // Render nothing until the session is known, or the app flashes the login
  // screen at someone who is already signed in.
  if (!ready) return null;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  if (!user.profile && (user.profiles?.length || 0) > 1) return <Profiles />;

  return children;
};

const HomeForUser = () => {
  const { user } = useAuth();
  return <Home username={user?.username} />;
};

/**
 * Each navigation to the player is a new sitting, even when the route pattern
 * is the same one — "next episode" moves /watch?src=A to /watch?src=B, and
 * without the key React would keep the mounted player with the previous
 * episode's probe, captions, position and release list all still in state.
 */
const FreshPlayer = () => {
  const location = useLocation();
  return <Player key={location.key} />;
};

// Accounts are back, and this login is the only gate: Cloudflare Access no
// longer sits in front of the tunnel. Sessions are an HttpOnly cookie; there is
// no open signup. Accounts are made on the server with scripts/create-user.js,
// or at /signup by someone holding an invite code from the admin panel.
const Routed = () => {
  const location = useLocation();
  // A title opened from a card is a modal over the page it was opened from:
  // the page keeps rendering for the *background* location and the title is
  // drawn on top. A title reached by URL has no background and renders alone.
  const background = location.state?.background;

  return (
    <>
      <Routes location={background || location}>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/link"
          element={
            <RequireAuth>
              <TvLink />
            </RequireAuth>
          }
        />

        {/* Playback is full-bleed, so it sits outside the header chrome */}
        <Route
          path="/player/:torrentId/:fileId"
          element={
            <RequireAuth>
              <FreshPlayer />
            </RequireAuth>
          }
        />
        <Route
          path="/watch"
          element={
            <RequireAuth>
              <FreshPlayer />
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          {/* The app starts at /home — `/` is the landing page, served by
              the server as a static file and never reached by this router. */}
          <Route path="/home" element={<HomeForUser />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/search" element={<Search />} />
          <Route path="/list" element={<MyList />} />
          <Route path="/sports" element={<Live />} />
          <Route path="/tv" element={<LiveTv />} />
          {/* The sports guide was briefly at /live; bookmarks from then still land */}
          <Route path="/live" element={<Navigate to="/sports" replace />} />
          <Route path="/title/:type/:id" element={<Title />} />
          <Route path="/person/:id" element={<Person />} />
          <Route path="/torrent/:torrentId" element={<TorrentDetails />} />
          <Route path="/data" element={<Data />} />
          <Route path="/admin" element={<Admin />} />
        </Route>

        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>

      {background && (
        <Routes>
          <Route
            path="/title/:type/:id"
            element={
              <RequireAuth>
                <Title />
              </RequireAuth>
            }
          />
        </Routes>
      )}
    </>
  );
};

/**
 * The opening plays once, straight after a successful sign-in — never on a
 * page load that finds a session already in place.
 */
const Opening = () => {
  const { opening, endOpening } = useAuth();
  return opening ? <Intro onDone={endOpening} /> : null;
};

const App = () => (
  <AuthProvider>
    <LibraryProvider>
      <CatalogProvider>
        <Routed />
        <Opening />
      </CatalogProvider>
    </LibraryProvider>
  </AuthProvider>
);

export default App;
